let db_instances: Map<string, IDBDatabase> = new Map();
let init_promises: Map<string, Promise<void>> = new Map();
let is_upgrading: Map<string, boolean> = new Map();
let transaction_queues: Map<string, ((value?: any) => void)[]> = new Map();
let running_transactions: Map<string, number> = new Map();
let store_locks: Map<string, Promise<void>> = new Map();

type IndexKeys = { last_updated_index: string; main_index: string };
type PaginatedT<T> = T & { $order_number: number };

function create_indexeddb_wrapper() {
	async function init(db_name: string): Promise<void> {
		if (init_promises.has(db_name)) {
			return init_promises.get(db_name)!;
		}

		const init_promise = new Promise<void>((resolve) => {
			const request = indexedDB.open(db_name);

			request.onupgradeneeded = () => {
				console.log(`Database '${db_name}' upgrade triggered.`);
			};

			request.onsuccess = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;
				db_instances.set(db_name, db);
				transaction_queues.set(db_name, []);
				running_transactions.set(db_name, 0);
				resolve();
			};

			request.onerror = (event) => {
				console.error(`Database error in '${db_name}': ${(event.target as IDBOpenDBRequest).error}`);
				resolve();
			};
		});

		init_promises.set(db_name, init_promise);
		return init_promise;
	}

	async function ensure_store_exists(
		db_name: string,
		store_name: string,
		index_keys: IndexKeys
	): Promise<boolean> {
		const lock_key = `${db_name}_${store_name}`;

		// If a lock exists, wait for it to resolve
		if (store_locks.has(lock_key)) {
			await store_locks.get(lock_key);
			return ensure_store_exists(db_name, store_name, index_keys); // Retry after the lock
		}

		// Initialize the lock
		const lock_promise = new Promise<void>(async (resolve) => {
			try {
				await init(db_name);

				const db = db_instances.get(db_name)!;

				// Check if the store exists
				if (db.objectStoreNames.contains(store_name)) {
					// Store exists, but an older schema may be missing the required
					// indexes. paginated_filter walks the index cursor while
					// paginated_filter_count walks the primary-key cursor, so a
					// missing index yields empty rows with a correct footer count.
					const missing = get_missing_indexes(db, store_name, index_keys);
					if (missing.length && !is_upgrading.get(db_name)) {
						await upgrade_db(db_name, db, store_name, index_keys);
					}
					return resolve();
				}

				// If an upgrade is needed, trigger it
				if (!is_upgrading.get(db_name)) {
					await upgrade_db(db_name, db, store_name, index_keys);
				}
			} catch (error) {
				console.error(`Error ensuring store '${store_name}' exists:`, error);
			} finally {
				store_locks.delete(lock_key); // Release the lock
				resolve();
			}
		});

		store_locks.set(lock_key, lock_promise);
		await lock_promise; // Wait for the lock to complete

		return true;
	}

	function get_missing_indexes(db: IDBDatabase, store_name: string, index_keys: IndexKeys): string[] {
		if (!index_keys) return [];
		const store = db.transaction(store_name, 'readonly').objectStore(store_name);
		const missing: string[] = [];
		if (index_keys.last_updated_index && !store.indexNames.contains(`${index_keys.last_updated_index}_index`))
			missing.push(index_keys.last_updated_index);
		if (
			index_keys.main_index &&
			index_keys.main_index !== index_keys.last_updated_index &&
			!store.indexNames.contains(`${index_keys.main_index}_index`)
		)
			missing.push(index_keys.main_index);
		return missing;
	}

	async function upgrade_db(db_name: string, db: IDBDatabase, store_name: string, index_keys: IndexKeys) {
		is_upgrading.set(db_name, true);

		// Wait for ongoing transactions to complete
		while (running_transactions.get(db_name) && running_transactions.get(db_name)! > 0) {
			await new Promise((resolve) => setTimeout(resolve, 50));
		}

		db.close();
		const new_version = db.version + 1;

		return new Promise<boolean>((resolve) => {
			const request = indexedDB.open(db_name, new_version);

			request.onupgradeneeded = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;

				let store: IDBObjectStore;
				if (!db.objectStoreNames.contains(store_name)) {
					store = db.createObjectStore(store_name, { keyPath: 'id' });
					console.log(`Object store '${store_name}' created in '${db_name}'.`);
				} else {
					// Existing store inside the version-change transaction.
					store = request.transaction!.objectStore(store_name);
				}

				if (index_keys && store) {
					if (index_keys.last_updated_index && !store.indexNames.contains(`${index_keys.last_updated_index}_index`))
						store.createIndex(`${index_keys.last_updated_index}_index`, index_keys.last_updated_index);
					if (
						index_keys.main_index &&
						index_keys.main_index !== index_keys.last_updated_index &&
						!store.indexNames.contains(`${index_keys.main_index}_index`)
					)
						store.createIndex(`${index_keys.main_index}_index`, index_keys.main_index);
				}
			};

			request.onsuccess = (event) => {
				const db = (event.target as IDBOpenDBRequest).result;
				db_instances.set(db_name, db);
				is_upgrading.set(db_name, false);
				process_transaction_queue(db_name); // Process queued transactions
				resolve(true);
			};

			request.onerror = (event) => {
				is_upgrading.set(db_name, false);
				process_transaction_queue(db_name); // Process queued transactions
				console.error('Error during upgrade:', (event.target as IDBOpenDBRequest).error);
				resolve(false);
			};
		});
	}

	function process_transaction_queue(db_name: string): void {
		const queue = transaction_queues.get(db_name);
		if (!queue || queue.length === 0) return;

		const task = queue.shift();
		if (task) {
			task();
			process_transaction_queue(db_name); // Process next in queue
		}
	}

	async function run_transaction<T>(
		db_name: string,
		store_name: string,
		mode: IDBTransactionMode,
		operation: (store: IDBObjectStore) => Promise<T>,
		index_keys: IndexKeys
	): Promise<T | null> {
		// Ensure the store exists before proceeding
		const store_exists = await ensure_store_exists(db_name, store_name, index_keys);
		if (!store_exists) {
			console.error(`Store '${store_name}' does not exist in database '${db_name}'.`);
			return null;
		}

		if (is_upgrading.get(db_name)) {
			await new Promise((resolve) => transaction_queues.get(db_name)?.push(resolve));
			return run_transaction(db_name, store_name, mode, operation, index_keys);
		}

		const db = db_instances.get(db_name)!;

		running_transactions.set(db_name, (running_transactions.get(db_name) || 0) + 1);

		try {
			const transaction = db.transaction([store_name], mode);
			const objectStore = transaction.objectStore(store_name);
			return await operation(objectStore);
		} catch (error: any) {
			if (error?.name === 'InvalidStateError') {
				return run_transaction(db_name, store_name, mode, operation, index_keys);
			}
			throw error;
		} finally {
			running_transactions.set(db_name, running_transactions.get(db_name)! - 1);
			if (running_transactions.get(db_name) === 0 && is_upgrading.get(db_name)) {
				process_transaction_queue(db_name);
			}
		}
	}


	async function get_one<T>(db_name: string, store_name: string, id: string, index_keys: IndexKeys): Promise<T | null> {
		return run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<T | null>((resolve) => {
				const request = store.get(id);

				request.onsuccess = () => resolve(request.result);
				request.onerror = () => {
					console.error('E343242!@: ', request.error)
					resolve(null);
				}
			});
		}, index_keys);
	}

	async function set_one<T>(db_name: string, store_name: string, document: T, index_keys: IndexKeys): Promise<boolean | null> {
		return run_transaction(db_name, store_name, 'readwrite', (store) => {
			return new Promise<boolean>((resolve) => {
				const request = store.put(document);

				request.onsuccess = (e) => resolve(true)
				request.onerror = () => resolve(false);
			});
		}, index_keys);
	}

	async function set_bulk<T>(db_name: string, store_name: string, documents: T[], index_keys: IndexKeys): Promise<boolean | null> {
		return run_transaction(db_name, store_name, 'readwrite', (store) => {
			return new Promise<boolean>((resolve) => {
				for (const document of documents) {
					const request = store.put(document);
					request.onerror = (event) => {
						console.error('## request.onerror d2d#2: ', (event.target as IDBTransaction).error)
						resolve(false);
					}
				}

				store.transaction.oncomplete = (e) => resolve(true)
				store.transaction.onerror = (event) => {
					console.error('## error at set_bulk $3432: ', (event.target as IDBTransaction).error)
					resolve(false)
				};
			});
		}, index_keys);
	}

	async function get_all<T>(db_name: string, store_name: string, index_keys: IndexKeys): Promise<T[]> {
		const op = await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<T[]>((resolve) => {

				const request = index_keys?.main_index ? store.index(`${index_keys?.main_index}_index`).getAll() : store.getAll(); // Open a cursor on the index

				request.onsuccess = () => resolve(request.result);
				request.onerror = () => {
					console.error('## error at get_all GFDJK543: ', request.error)
					resolve([]);
				}
			});
		}, index_keys);
		return op || []
	}


	async function keys(db_name: string, store_name: string, index_keys: IndexKeys): Promise<string[]> {
		const op = await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<string[]>((resolve) => {
				const request = store.getAllKeys();
				request.onsuccess = () => resolve(request.result as string[]);
				request.onerror = () => resolve([]);
			});
		}, index_keys);
		return op || []
	}

	async function clear_store(db_name: string, store_name: string, index_keys: IndexKeys): Promise<boolean> {
		const op = await run_transaction(db_name, store_name, 'readwrite', (store) => {
			return new Promise<boolean>((resolve) => {
				const request = store.clear();

				request.onsuccess = () => resolve(true);
				request.onerror = () => resolve(false);
			});
		}, index_keys);

		return op || false

	}


	async function last_key_value<T>(
		db_name: string,
		store_name: string,
		index_keys: IndexKeys,
	): Promise<T | null> {
		const op = await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<T | null>((resolve) => {
				// res
				const index = store.index(`${index_keys.main_index}_index`); // Access the index
				const request = index.openCursor(null, 'prev'); // Iterate in ascending order

				request.onsuccess = () => {
					const cursor = request.result;
					if (cursor) resolve(cursor.value as T) // Add the key to the array
					else resolve(null)
				};

				request.onerror = () => resolve(null);
			})
		}, index_keys)

		return op
	}

	async function last_updated_key_value<T>(
		db_name: string,
		store_name: string,
		index_keys: IndexKeys,
	): Promise<T | null> {
		const op = await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<T | null>((resolve) => {
				// res
				const index = store.index(`${index_keys.last_updated_index}_index`); // Access the index
				const request = index.openCursor(null, 'prev'); // Iterate in ascending order

				request.onsuccess = () => {
					const cursor = request.result;
					if (cursor) resolve(cursor.value as T) // Add the key to the array
					else resolve(null)
				};

				request.onerror = () => resolve(null);
			})
		}, index_keys)

		return op
	}


	async function paginate<T, K extends IndexKeys>(
		db_name: string,
		store_name: string,
		page: number,
		page_size: number,
		index_keys: K,
		index_to_use: Extract<K[keyof K], string>,

	): Promise<PaginatedT<T>[]> {
		const op = await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<PaginatedT<T>[]>((resolve) => {

				if (page < 1) return resolve([])

				const request = index_to_use ? store.index(`${index_to_use}_index`).openCursor(null, 'prev') : store.openCursor(null, 'prev'); // Open a cursor on the index

				const results: PaginatedT<T>[] = [];
				let cursor_position_set = false

				request.onsuccess = () => {
					const cursor = request.result;
					if (!cursor) return resolve(results); // Resolve when no more records are available

					if (page !== 1 && !cursor_position_set) {
						// console.log('############## setting to position:' + (page - 1) * page_size)
						cursor_position_set = true
						cursor.advance((page - 1) * page_size); // Move the cursor forward by `page` records
						return
					}

					results.push({ ...cursor.value as T, $order_number: ((page - 1) * page_size) + results.length + 1 }); // Add the current value to the result

					if (results.length >= page_size) {
						return resolve(results);
					}
					cursor.continue(); // Move to the next record
				};

				request.onerror = () => {
					console.error('sads#@$#@: ', request.error)
					resolve([]);
				}
			});
		}, index_keys);

		return op ?? []
	}

	async function filter<T, K extends IndexKeys>(
		db_name: string,
		store_name: string,
		filter_fn: (item: T) => boolean,
		index_keys: K,
		return_fields?: Array<keyof T>,
		index_to_use?: Extract<K[keyof K], string>,
	): Promise<T[]> {
		const op = await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<T[]>((resolve) => {
				const results: T[] = [];

				const cursorRequest = index_to_use ? store.index(`${index_to_use}_index`).openCursor(null, 'prev') : store.openCursor(null, 'prev');

				cursorRequest.onsuccess = (event) => {
					const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
					if (cursor) {
						const item = cursor.value as T;
						if (filter_fn(item)) {
							if (return_fields && return_fields.length > 0) {
								const partial_item = {} as any;
								return_fields.forEach((key) => { partial_item[key as keyof typeof partial_item] = item[key]; });
								results.push(partial_item);

							} else results.push(item);
						}
						cursor.continue();
					} else {
						resolve(results); // Resolve when all records are processed
					}
				};

				cursorRequest.onerror = (event) => {
					console.error('doaspdos $#@$#@: ', (event.target as IDBRequest).error)
					resolve([]);
				}
			})
		}, index_keys)

		return op ?? []
	}

	async function filter_count<T, K extends IndexKeys>(
		db_name: string,
		store_name: string,
		filter_fn: (item: T) => boolean,
		index_keys: K,
		index_to_use?: Extract<K[keyof K], string>,
	): Promise<number> {
		let count = 0
		const op = await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<number>((resolve) => {

				const cursorRequest = index_to_use ? store.index(`${index_to_use}_index`).openCursor(null, 'prev') : store.openCursor(null, 'prev');

				cursorRequest.onsuccess = (event) => {
					const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
					if (cursor) {
						const item = cursor.value as T;
						if (filter_fn(item)) {
							count++;
						}
						cursor.continue();
					} else {
						resolve(count); // Resolve when all records are processed
					}
				};

				cursorRequest.onerror = (event) => {
					console.error('doaspdos $#@$#@: ', (event.target as IDBRequest).error)
					resolve(0);
				}
			})
		}, index_keys)

		return op ?? 0
	}

	// PAGINATE SEARCH 

	/**
 * Counts the total number of items in a store that match a filter.
 * This is useful for calculating total pages for the UI.
 */
	async function paginated_filter_count<T>(
		db_name: string,
		store_name: string,
		filter_fn: (item: T) => boolean,
		index_keys: IndexKeys,
		index_to_use?: keyof T
	): Promise<number> {
		return await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<number>((resolve, reject) => {
				let count = 0;
				const index = index_to_use ? store.index(`${String(index_to_use)}_index`) : null;
				const request = index ? index.openCursor() : store.openCursor();

				request.onsuccess = () => {
					const cursor = request.result;
					if (cursor) {
						if (filter_fn(cursor.value)) {
							count++;
						}
						cursor.continue();
					} else {
						resolve(count);
					}
				};
				request.onerror = (event) => reject((event.target as IDBRequest).error);
			});
		}, index_keys) ?? 0;
	}

	/**
	 * Fetches a page of filtered results, always in DESCENDING order by the specified index.
	 */

	async function paginated_filter<T, K extends IndexKeys>(
		db_name: string,
		store_name: string,
		options: {
			page: number;
			page_size: number;
			filter_fn: (item: T) => boolean;
			index_keys: K;
			index_to_use?: Extract<keyof T, string>;
			cursor_key?: IDBValidKey;
			sort_direction: 'asc' | 'desc';
		}
	): Promise<[
		values: PaginatedT<T>[],
		first_index_key: IDBValidKey | undefined,
		last_index_key: IDBValidKey | undefined,
		has_more: boolean
	]> {
		const { page, page_size, filter_fn, sort_direction, index_keys, index_to_use, cursor_key } = options;

		const op = await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<[PaginatedT<T>[], IDBValidKey | undefined, IDBValidKey | undefined, boolean]>((resolve) => {
				const results: PaginatedT<T>[] = [];
				let items_to_skip = (page - 1) * page_size;
				let has_more = false;

				// These will store the INDEX key from the cursor, not the primary key.
				let first_key: IDBValidKey | undefined = undefined;
				let last_key: IDBValidKey | undefined = undefined;

				const cursor_direction = sort_direction === 'asc' ? 'next' : 'prev';

				const key_range = !cursor_key ? null :
					cursor_direction === 'prev' ?
						IDBKeyRange.upperBound(cursor_key, true)
						: IDBKeyRange.lowerBound(cursor_key, true)
					;

				let index: IDBIndex | null = null;
				if (index_to_use) {
					try {
						index = store.index(`${String(index_to_use)}_index`);
					} catch {
						// Index missing on a legacy store — fall back to the
						// primary-key cursor so rows still render. ensure_store_exists
						// now creates the index, so this is a safety net only.
						index = null;
					}
				}
				const request = index ? index.openCursor(key_range, cursor_direction) : store.openCursor(key_range, cursor_direction);

				const should_skip = !cursor_key;

				request.onsuccess = () => {
					const cursor = request.result;
					if (!cursor) {
						resolve([results, first_key, last_key, false]);
						return;
					}

					const item = cursor.value as T;

					if (filter_fn(item)) {
						if (should_skip && items_to_skip > 0) {
							items_to_skip--;
							return cursor.continue();
						}

						if (results.length < page_size) {
							// **CRITICAL CHANGE**: Capture the cursor's key (the index key).
							if (first_key === undefined) {
								first_key = cursor.key;
							}
							last_key = cursor.key; // Always update to the latest item's index key.

							results.push({
								...item,
								$order_number: ((page - 1) * page_size) + results.length + 1,
							});
						} else {
							has_more = true;
							resolve([results, first_key, last_key, has_more]);
							if (request.transaction) request.transaction.abort();
							return;
						}
					}
					cursor.continue();
				};

				request.onerror = (event) => {
					console.error('Error in paginated_filter:', (event.target as IDBRequest).error);
					resolve([[], undefined, undefined, false]);
				};
			});
		}, index_keys);

		return op ?? [[], undefined, undefined, false];
	}


	/**
	 * Counts how many matching items have a key GREATER than the target_key.
	 * In a descending list, this tells you how many new items have been inserted "before" your current view,
	 * allowing you to recalculate the correct page number.
	 */
	async function count_items_after_key<T>(
		db_name: string,
		store_name: string,
		filter_fn: (item: T) => boolean,
		index_keys: IndexKeys,
		target_key: IDBValidKey,
		index_to_use?: keyof T
	): Promise<number> {
		return await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<number>((resolve, reject) => {
				let count = 0;
				// Range for all keys greater than our target key.
				const key_range = IDBKeyRange.lowerBound(target_key, true); // 'true' to exclude the key itself

				const index = index_to_use ? store.index(`${String(index_to_use)}_index`) : null;
				const request = index ? index.openCursor(key_range, 'prev') : store.openCursor(key_range, 'prev');

				request.onsuccess = () => {
					const cursor = request.result;
					if (cursor) {
						if (filter_fn(cursor.value)) {
							count++;
						}
						cursor.continue();
					} else {
						resolve(count); // End of records
					}
				};
				request.onerror = (event) => reject((event.target as IDBRequest).error);
			});
		}, index_keys) ?? 0;
	}

	async function remove_one(db_name: string, store_name: string, id: string, index_keys: IndexKeys): Promise<boolean> {
		const op = await run_transaction(db_name, store_name, 'readwrite', (store) => {
			return new Promise<boolean>((resolve) => {
				const request = store.delete(id);
				request.onsuccess = () => resolve(true);
				request.onerror = () => resolve(false);
			});
		}, index_keys)
		return op ?? false
	}


	/**
	 * Counts the number of records in the specified database and store.
	 */
	async function count(db_name: string, store_name: string, index_keys: IndexKeys): Promise<number> {
		const op = await run_transaction(db_name, store_name, 'readonly', (store) => {
			return new Promise<number>((resolve) => {
				const request = store.count();
				request.onsuccess = () => resolve(request.result);
				request.onerror = () => resolve(0);
			})
		}, index_keys)

		return op ?? 0

	}


	return {
		get_one,
		set_one,
		set_bulk,
		count_items_after_key,
		get_all,
		keys,
		clear_store,
		last_key_value,
		last_updated_key_value,
		paginate,
		filter,
		remove_one,
		filter_count,
		run_transaction,
		paginated_filter,
		paginated_filter_count,
		count
	}
};

const indexeddb_wrapper = create_indexeddb_wrapper()
export default indexeddb_wrapper;